/**
 * Utility functions for generating standardized filenames across the application
 */

interface ProfileExportOptions {
  firstName?: string;
  lastName?: string;
  documentId?: string;
  format?: 'pdf' | 'md';
  variant?: 'standard' | 'ic';
}

interface ProposalExportOptions {
  clientName: string;
  firstName?: string;
  lastName?: string;
  format: 'pdf' | 'pptx';
}

/**
 * Stores version counters for each date to ensure unique filenames
 */
const versionCounters: Record<string, Record<string, number>> = {};

/**
 * Gets the next version number for a given base filename on the current date
 */
const getVersionNumber = (baseKey: string, date: string): number => {
  if (!versionCounters[date]) {
    versionCounters[date] = {};
  }

  if (!versionCounters[date][baseKey]) {
    versionCounters[date][baseKey] = 1;
  } else {
    versionCounters[date][baseKey]++;
  }

  return versionCounters[date][baseKey];
};

/**
 * Formats a name for use in filenames (removes special characters, converts to uppercase)
 * Handles UTF-8 characters by converting them to ASCII equivalents
 */
const formatNameForFilename = (name: string | undefined): string => {
  if (!name) return 'UNKNOWN';

  // Normalize unicode characters to their ASCII equivalents
  // This converts characters like ö to o, é to e, etc.
  const normalized = name.normalize('NFD').replace(/[\u0300-\u036f]/g, '');

  // Convert to uppercase and remove any remaining non-alphanumeric characters
  // Keep it simple - just letters and numbers, no spaces
  return normalized.toUpperCase().replace(/[^A-Z0-9]/g, '');
};

/**
 * Gets the current date in YYYYMMDD format
 */
const getCurrentDateString = (): string => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}${month}${day}`;
};

const getProfileFilenamePrefix = (variant: ProfileExportOptions['variant'] = 'standard'): string => {
  return variant === 'ic' ? 'IC_CV' : 'PP';
};

/**
 * Generates a filename for professional profile exports.
 * Standard profiles use PP_* while provider/contractor flows can opt into IC_CV_*.
 */
export const generateProfileFilename = (options: ProfileExportOptions): string => {
  const firstName = formatNameForFilename(options.firstName);
  const lastName = formatNameForFilename(options.lastName);
  const date = getCurrentDateString();
  const format = options.format || 'pdf';
  const prefix = getProfileFilenamePrefix(options.variant);

  // Create a unique key for this person
  const baseKey = `${prefix}_${firstName}_${lastName}`;
  const version = getVersionNumber(baseKey, date);

  return `${prefix}_${firstName}_${lastName}_${date}_${version}.${format}`;
};

/**
 * Generates a filename for Proposal exports
 * Format: CLIENT_FIRST_LAST_DATE_V.pdf/pptx
 * Example: MICROSOFT_JOHN_DOE_20240115_1.pdf
 */
export const generateProposalFilename = (options: ProposalExportOptions): string => {
  const clientName = formatNameForFilename(options.clientName);
  const firstName = formatNameForFilename(options.firstName);
  const lastName = formatNameForFilename(options.lastName);
  const date = getCurrentDateString();

  // If we have individual names, include them
  let baseKey: string;
  let filename: string;

  if (firstName && lastName && firstName !== 'UNKNOWN' && lastName !== 'UNKNOWN') {
    baseKey = `${clientName}_${firstName}_${lastName}`;
    const version = getVersionNumber(baseKey, date);
    filename = `${clientName}_${firstName}_${lastName}_${date}_${version}.${options.format}`;
  } else {
    // For team proposals without individual names
    baseKey = `${clientName}_TEAM`;
    const version = getVersionNumber(baseKey, date);
    filename = `${clientName}_TEAM_${date}_${version}.${options.format}`;
  }

  return filename;
};

/**
 * Extracts name from user display name or email
 */
export const extractNameFromUser = (displayName?: string, email?: string): { firstName: string; lastName: string } => {
  let firstName = '';
  let lastName = '';

  if (displayName) {
    // Try to split display name
    const parts = displayName.trim().split(/\s+/);
    if (parts.length >= 2) {
      firstName = parts[0];
      lastName = parts[parts.length - 1];
    } else if (parts.length === 1) {
      firstName = parts[0];
    }
  } else if (email) {
    // Try to extract from email (e.g., john.doe@company.com)
    const localPart = email.split('@')[0];
    const parts = localPart.split(/[._-]/);
    if (parts.length >= 2) {
      firstName = parts[0];
      lastName = parts[1];
    } else {
      firstName = localPart;
    }
  }

  return { firstName, lastName };
};

/**
 * Resets version counters for a new day (optional utility)
 */
export const resetVersionCounters = (): void => {
  const today = getCurrentDateString();
  // Keep only today's counters
  Object.keys(versionCounters).forEach(date => {
    if (date !== today) {
      delete versionCounters[date];
    }
  });
};

/**
 * Gets a fallback filename if user information is not available
 */
export const getFallbackFilename = (
  type: 'profile' | 'proposal',
  format: string,
  documentId?: string,
  profileVariant: ProfileExportOptions['variant'] = 'standard'
): string => {
  const date = getCurrentDateString();
  const id = documentId ? `_${documentId.slice(0, 8)}` : '';

  if (type === 'profile') {
    return `${getProfileFilenamePrefix(profileVariant)}_EXPORT${id}_${date}.${format}`;
  } else {
    return `PROPOSAL_EXPORT${id}_${date}.${format}`;
  }
};

/**
 * Extracts name from markdown content by looking for common patterns
 * Looks for patterns like:
 * - # John Doe (heading)
 * - **Name:** John Doe
 * - **John Doe** (bold at start)
 * - Name: John Doe
 */
export const extractNameFromMarkdown = (markdown: string): { firstName: string; lastName: string } => {
  if (!markdown) {
    return { firstName: '', lastName: '' };
  }

  // Try different patterns to find the name
  // Now includes Unicode letter support for names with special characters
  const patterns = [
    /^#\s+([\p{L}][\p{L}]+(?:\s+[\p{L}][\p{L}]+)+)/mu,  // # John Doe (heading at start)
    /^\*\*([\p{L}][\p{L}]+(?:\s+[\p{L}][\p{L}]+)+)\*\*/mu,  // **John Doe** at start
    /^###?\s+([\p{L}][\p{L}]+(?:\s+[\p{L}][\p{L}]+)+)/mu,  // ## or ### John Doe
    /\*\*Name[:\s]+([\p{L}][\p{L}]+(?:\s+[\p{L}][\p{L}]+)+)\*\*/iu,  // **Name: John Doe**
    /Name[:\s]+([\p{L}][\p{L}]+(?:\s+[\p{L}][\p{L}]+)+)/iu,  // Name: John Doe
    /^([\p{L}][\p{L}]+(?:\s+[\p{L}][\p{L}]+)+)\s*\n/mu,  // John Doe at the very start
  ];

  for (const pattern of patterns) {
    const match = markdown.match(pattern);
    if (match && match[1]) {
      const fullName = match[1].trim();
      const parts = fullName.split(/\s+/);
      if (parts.length >= 2) {
        return {
          firstName: parts[0],
          lastName: parts[parts.length - 1]
        };
      }
    }
  }

  // If no name found in content, return empty
  return { firstName: '', lastName: '' };
};
